import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { subscribeClientPortalUpdates } from "../services/clientPortalService";

function asDate(value) {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value, includeTime = false) {
  const date = asDate(value);
  if (!date) return "—";
  return date.toLocaleString("en-IN", includeTime
    ? { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "short", year: "numeric" });
}

export default function ClientPortalWorkUpdates() {
  const { userData } = useAuth();
  const [updates, setUpdates] = useState([]);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!userData?.clientId) return undefined;
    return subscribeClientPortalUpdates(userData.clientId, setUpdates, (e) => setError(e.message || "Unable to load work updates."));
  }, [userData?.clientId]);

  const references = useMemo(() => Array.from(new Set(updates.map((item) => item.reference || item.title).filter(Boolean))), [updates]);
  const visible = filter === "all" ? updates : updates.filter((item) => (item.reference || item.title) === filter);

  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#7F9BB3]">Work Updates</p>
          <h1 className="mt-2 text-3xl font-semibold">Published Updates</h1>
          <p className="mt-2 max-w-3xl text-slate-600">This timeline contains only updates that your firm explicitly published for client viewing.</p>
        </div>
        {references.length > 1 && (
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-lg border bg-white px-3 py-2 text-sm">
            <option value="all">All work items</option>
            {references.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        )}
      </div>

      {error && <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}

      <div className="mt-7 space-y-4">
        {!visible.length && <div className="rounded-2xl border bg-white p-8 text-center text-sm text-slate-500 shadow-sm">No update has been published to you yet.</div>}
        {visible.map((item) => (
          <article key={item.id} className="rounded-2xl border bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{item.reference || "Work item"}</p>
                <h2 className="mt-1 text-lg font-semibold">{item.title || "Work update"}</h2>
              </div>
              <div className="text-right">
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{item.clientStatus || "In progress"}</span>
                <p className="mt-2 text-xs text-slate-400">Published {formatDate(item.publishedAt, true)}</p>
              </div>
            </div>
            <p className="mt-4 whitespace-pre-wrap text-sm leading-6 text-slate-700">{item.clientUpdate}</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {(item.nextAction || item.nextActionDate) && (
                <div className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
                  <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Next Action</p>
                  <p className="mt-1">{item.nextAction || "Action scheduled"}</p>
                  {item.nextActionDate && <p className="mt-1 text-xs">Due: {formatDate(item.nextActionDate)}</p>}
                </div>
              )}
              {item.hearingDate && (
                <div className="rounded-xl bg-blue-50 p-3 text-sm text-blue-900">
                  <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Hearing / Important Date</p>
                  <p className="mt-1">{formatDate(item.hearingDate)}</p>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
